import React from 'react';

import GamAdUnit from '@client/adManager/AdUnit/GamAdUnit.js';
import { $config } from '@client/core/atoms/config.js';
import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import {
  $placementsMap,
  updatePlacementKeyValueById
} from '@client/core/atoms/placements.js';
import { getInViewSettingsByBrand } from '@client/core/utils/getInViewSettingsByPlacementId.js';
import { isRetailMediaOverride } from '@client/core/utils/retailMediaUtils.js';
import { i18n } from '@lingui/core';
import {
  AdServer,
  ContainerId,
  type PlacementId,
  Placements,
  PlacementStatus,
  debugLog
} from '@schibsted-nmp/advertising-shared';

import { SponsoredProductCarousel } from '../ads/SponsoredProductCarousel.js';
import {
  DsaBrand,
  type RetailMediaPayload,
  type RetailMediaProduct
} from '../types/index.js';
import {
  callApi,
  RateLimitError,
  getVisitorId,
  isRateLimited
} from '../utils/RetailMediaApi.js';
import { clearSlot, renderIntoSlot } from '../utils/slotRoot.js';

/**
 * The carousel pages three cards at a time, so it asks for enough products to
 * fill a few pages. The server returns fewer when the keyword ladder runs dry.
 */
const SPONSORED_PRODUCTS_COUNT = 10;

/** Longest a GAM-backed placement waits for retail media before backfilling */
const GAM_BACKFILL_TIMEOUT_MS = 1000;

// Placements showing the GAM backfill instead of the carousel
const gamBackfilled = new Set<PlacementId>();

function getContainerElement(placementId: PlacementId) {
  return document.getElementById(
    ContainerId.fromPlacementId(placementId, AdServer.Retailmedia)
  );
}

/**
 * Renders the GAM unit into the carousel's slot, for GAM placements the config
 * API flagged to try retail media first. Returns false when there is no GAM
 * placement to backfill with.
 */
export function renderGamBackfill(placementId: PlacementId): boolean {
  const placement = $placementsMap.get()[placementId];
  const containerElement = getContainerElement(placementId);

  if (
    !containerElement ||
    !placement ||
    !isRetailMediaOverride(placement) ||
    !Placements.Gam.is(placement)
  ) {
    return false;
  }

  debugLog('retail_media -> GAM backfill for placement:', placementId);

  // Restore the banner semantics a previous carousel removed from the host
  const host = containerElement.closest('advt-component');
  host?.setAttribute('role', 'img');
  host?.setAttribute(
    'title',
    i18n.t({ id: 'native.advertisement', message: 'advertisement' })
  );

  // pending lets the GAM unit request its slot once it is in view
  updatePlacementKeyValueById(placementId, 'status', 'pending');
  updatePlacementKeyValueById(placementId, 'creativeType', 'banner');
  renderIntoSlot(
    placementId,
    containerElement,
    <GamAdUnit
      placement={placement}
      inViewSettings={getInViewSettingsByBrand(placementId)}
    />
  );
  gamBackfilled.add(placementId);

  return true;
}

function handleNoFill(placementId: PlacementId) {
  updateMetricByKey(placementId, PlacementStatus.AdNoFill);

  if (renderGamBackfill(placementId)) return;

  const containerElement = getContainerElement(placementId);
  if (containerElement) clearSlot(placementId, containerElement);
  updatePlacementKeyValueById(placementId, 'status', 'error');
}

function rejectAfter(ms: number) {
  return new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Timed out after ${ms}ms`));
    }, ms);
  });
}

const request = async (
  placementId: PlacementId,
  searchQuery: string,
  categories: string[] | undefined,
  brandNames?: Record<string, string>
) => {
  const brand = $config.get()?.brand;
  const dsaBrand = DsaBrand.is(brand) ? brand : undefined;
  const hasGamBackfill = isRetailMediaOverride(
    $placementsMap.get()[placementId]
  );

  // A rate-limited request renders nothing, so keep whatever the slot shows
  if (isRateLimited()) {
    debugLog('retail_media -> Rate-limited request ignored:', placementId);
    return;
  }

  // Unmount the backfill before a new search refreshes it, so it doesn't
  // request a GAM ad the carousel may replace
  const containerElement = getContainerElement(placementId);
  if (gamBackfilled.delete(placementId) && containerElement) {
    clearSlot(placementId, containerElement);
  }

  try {
    const payload: RetailMediaPayload = {
      zone: 'recommerce_search',
      visitorId: getVisitorId(),
      sponsoredProductsCount: SPONSORED_PRODUCTS_COUNT,
      searchTerm: searchQuery,
      ...(categories && categories.length > 0 && { categories }),
      ...(brandNames &&
        Object.keys(brandNames).length > 0 && {
          brandNames: Object.values(brandNames)
        }),
      debug: true,
      userAgent: navigator.userAgent,
      ...(dsaBrand && { dsaBrand })
    };

    updatePlacementKeyValueById(placementId, 'creativeType', 'native-retail');
    updatePlacementKeyValueById(placementId, 'status', 'request');

    const dataPromise = callApi(payload);

    debugLog(
      `retail_media -> ad requested for placement: ${placementId}`,
      payload
    );

    updatePlacementKeyValueById(placementId, 'status', 'requested');
    updateMetricByKey(placementId, PlacementStatus.AdRequested);

    const data = await (hasGamBackfill
      ? Promise.race([dataPromise, rejectAfter(GAM_BACKFILL_TIMEOUT_MS)])
      : dataPromise);

    debugLog('retail_media -> API response:', data);

    setTimeout(() => {
      // The query was cleared while this request was pending
      if (gamBackfilled.has(placementId)) return;

      try {
        renderSponsoredProductCarousel(
          placementId,
          data.sponsoredProducts,
          categories
        );
      } catch (error) {
        debugLog(
          'retail_media -> Error rendering sponsored carousel ad:',
          error
        );

        handleNoFill(placementId);
      }
    }, 1);
  } catch (error) {
    // Ignore rate-limited requests - the first request will handle rendering
    if (error instanceof RateLimitError) {
      debugLog('retail_media -> Rate-limited request ignored:', error.message);
      return;
    }

    debugLog('retail_media -> Error in request:', error);
    if (gamBackfilled.has(placementId)) return;

    handleNoFill(placementId);
  }
};

export function triggerCarouselRequest(
  placementId: PlacementId,
  searchQuery: string | string[] | undefined,
  categories: string[] | undefined,
  brandNames?: Record<string, string>
) {
  const normalizedQuery = Array.isArray(searchQuery)
    ? searchQuery[0]
    : searchQuery;

  void request(placementId, normalizedQuery || '', categories, brandNames);
}

function renderSponsoredProductCarousel(
  placementId: PlacementId,
  products?: Array<RetailMediaProduct>,
  categories?: string[]
) {
  const containerElement = getContainerElement(placementId);

  if (!containerElement) {
    debugLog('retail_media -> Container element not found:', placementId);
    updateMetricByKey(placementId, PlacementStatus.NoElementInDom);
    updatePlacementKeyValueById(placementId, 'status', 'error');
    return;
  }

  const [firstProduct, ...restProducts] = products ?? [];

  // Handle no-fill case
  if (!firstProduct) {
    debugLog('retail_media -> No-fill case for placement:', placementId);
    handleNoFill(placementId);
    return;
  }

  // The carousel is interactive and labels itself; the host's role="img"
  // would hide its buttons and links from assistive tech.
  const host = containerElement.closest('advt-component');
  host?.removeAttribute('role');
  host?.removeAttribute('title');

  // Note: the grid item is the search page's own `.retail-media-ad-wrapper`,
  // not this element, so column spanning is done from CSS
  // (see `.retail-media-ad-wrapper:has(.rm-carousel)`).
  renderIntoSlot(
    placementId,
    containerElement,
    <SponsoredProductCarousel
      products={[firstProduct, ...restProducts]}
      placementId={placementId}
      categories={categories}
    />
  );
}
